/* ==========================================================================
   KPod product-page CRO redesign — behaviour + instrumentation
   --------------------------------------------------------------------------
   A/B flag: product-page-redesign   |   treatment value: 'redesign'

   Loaded on product pages only. Does nothing at all unless the visitor is in
   the treatment: layout/theme.liquid resolves the flag and fires
   `kpod:pp-variant` on document, and everything below waits for that.

   Config comes from the JSON blob that snippets/kpod-pp-data.liquid renders
   (prices, pack maths, variant ids, thresholds) so nothing here scrapes the
   DOM for numbers or hardcodes a price.

   Force a variant locally:
     posthog.featureFlags.overrideFeatureFlags({ flags: {'product-page-redesign': 'redesign'} })
   or, with no PostHog at all, append ?pp=redesign / ?pp=control to the URL.
   ========================================================================== */
(function () {
  'use strict';

  var TREATMENT = 'redesign';
  var CAROUSEL_MS = 3200;
  var PACK_RETRY_MS = 100;
  var PACK_RETRY_MAX = 40;

  // ------------------------------------------------------------------------
  // Config
  // ------------------------------------------------------------------------
  var CFG = (function () {
    var el = document.getElementById('kpod-pp-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  })();

  // ------------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------------
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function money(pence) {
    var currency = (CFG && CFG.currency) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency
      }).format(pence / 100);
    } catch (e) {
      return '£' + (pence / 100).toFixed(2);
    }
  }

  /* Every event carries the variant and enough product context to slice on,
     so the test can be judged on more than raw add-to-cart clicks. */
  function capture(event, props) {
    if (typeof posthog === 'undefined' || !posthog.capture) return;
    var base = {
      pp_variant: TREATMENT,
      pp_experiment: 'product-page-redesign',
      product_type: CFG ? CFG.type : null,
      product_id: CFG ? CFG.productId : null,
      product_handle: CFG ? CFG.handle : null,
      template: CFG ? CFG.template : null
    };
    if (props) {
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k)) base[k] = props[k];
      }
    }
    try {
      posthog.capture(event, base);
    } catch (e) { /* analytics must never break the page */ }
  }

  /* Fire once per element, the first time it is at least half on screen. */
  function onceVisible(el, fn) {
    if (!el) return;
    if (!('IntersectionObserver' in window)) { fn(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { io.disconnect(); fn(); }
      });
    }, { threshold: 0.5 });
    io.observe(el);
  }

  function packFor(value) {
    if (!CFG || !CFG.packs) return null;
    for (var i = 0; i < CFG.packs.length; i++) {
      if (String(CFG.packs[i].value).toLowerCase() === String(value).toLowerCase()) return CFG.packs[i];
    }
    return null;
  }

  /* The authoritative selection is whatever the shared radio group holds — it
     may be the theme's control radio rather than the visible card, since the
     theme owns that state. Read the group, then map back to the pack value. */
  function selectedPackInput() {
    var picker = $('hdt-variant-picker') || document;
    return picker.querySelector('input[is-value]:checked')
      || $('.hdt-ab-variant-cards .hdt-pod-card__input:checked');
  }

  // ------------------------------------------------------------------------
  // 0. Buy-column ordering fallback
  // The redesign blocks are wrapped in .hdt-product-info__item by the theme, so
  // the CSS `order` has to sit on the wrapper. The stylesheet does that with
  // :has(); this stamps the same value as an attribute so the buy box is still
  // in the right order in a browser without :has() support. Cheap insurance on
  // the one thing that would be very visibly wrong if it failed.
  // ------------------------------------------------------------------------
  var ORDER_MAP = [
    ['.kpod-pp-rating', 2],
    ['.kpod-pp-value', 3],
    ['.kpod-pp-tiles', 6],
    ['.kpod-pp-kit', 6],
    ['.kpod-pp-packhead', 7],
    ['.kpod-pp-nudge', 9],
    ['.kpod-pp-trust', 13],
    ['.kpod-pp-pay', 14],
    ['.kpod-pp-compat', 15]
  ];

  function initOrder() {
    ORDER_MAP.forEach(function (pair) {
      $$(pair[0]).forEach(function (el) {
        var wrap = el.closest ? el.closest('.hdt-product-info__item') : null;
        if (wrap) wrap.setAttribute('data-pp-order', String(pair[1]));
      });
    });
  }

  // ------------------------------------------------------------------------
  // 0b. CTA row
  // The design puts the quantity stepper, the add-to-basket button and the
  // wishlist button on one line, with Shop Pay full width beneath. The theme
  // renders the stepper as its own block above the buy buttons, so it is moved
  // into the button row here — one node move, nothing cloned, the input keeps
  // its name/form attributes so the cart contract is untouched.
  // The label is also corrected to the design's wording and given its bag icon.
  // ------------------------------------------------------------------------
  /* Move the per-pod meta and Klarna into the theme's price block so the three
     always share one baseline. They are rendered as separate blocks because a
     custom_liquid block cannot be placed inside another block's markup. */
  /* The bundle shows its price inside the value card. Rather than rendering a
     second price and hiding the theme's, MOVE the theme's block into the card:
     one element, always the live store price, no state where both can show. */
  function initValueCard() {
    var card = $('.kpod-pp-value');
    var price = $('.hdt-product__price');
    if (!card || !price || card.contains(price)) return;
    card.insertBefore(price, card.firstChild);
    var wrap = price.closest('.hdt-product-info__item');
    if (wrap && wrap !== price && !wrap.querySelector('[data-kpod-pp], [data-kpod-pp-flex], [data-kpod-pp-grid]')
        && (wrap.textContent || '').trim() === '') {
      wrap.style.display = 'none';
    }
  }

  function initPriceLine() {
    var price = $('.hdt-product__price');
    if (!price) return;

    /* Collapse a custom_liquid wrapper only once nothing of ours is left in it —
       these wrappers are shared, and hiding one that still holds another block
       takes that block off the page too. */
    function hideEmptyWrapper(wrap) {
      if (!wrap || wrap === price) return;
      if (wrap.querySelector('[data-kpod-pp], [data-kpod-pp-flex], [data-kpod-pp-grid]')) return;
      if ((wrap.textContent || '').trim() !== '') return;
      wrap.style.display = 'none';
    }

    // The per-pod meta always belongs on the price line.
    var perPod = $('.kpod-pp-perpod');
    if (perPod && !price.contains(perPod)) {
      var perWrap = perPod.closest('.hdt-product-info__item');
      price.appendChild(perPod);
      hideEmptyWrapper(perWrap);
    }

    /* Klarna sits ON the price line on desktop, but the mobile design puts it
       centred BELOW the Shop Pay button. Re-home it on resize so a rotation or
       a resized window lands in the right place either way.

       Only the STANDALONE Klarna block is eligible. The bundle renders its own
       Klarna line inside the value card (.kpod-pp-value__klarna); picking that
       one up moved it out of the card and then hid the card's wrapper as its
       "home", which silently took the entire bundle price block off the page. */
    var klarna = $('.kpod-pp-klarna:not(.kpod-pp-value__klarna)');
    if (!klarna || klarna.closest('.kpod-pp-value')) return;
    var home = klarna.closest('.hdt-product-info__item');
    // Never hide a wrapper that carries other treatment content.
    if (home && home.querySelectorAll('[data-kpod-pp], [data-kpod-pp-flex], [data-kpod-pp-grid]').length > 1) {
      home = null;
    }
    var mq = window.matchMedia('(min-width: 768px)');

    function place() {
      if (mq.matches) {
        if (!price.contains(klarna)) {
          price.appendChild(klarna);
          if (home) home.style.display = 'none';
        }
      } else if (home && !home.contains(klarna)) {
        home.appendChild(klarna);
        home.style.display = '';
      }
    }
    place();
    if (mq.addEventListener) mq.addEventListener('change', place);
  }

  /* On machine and bundle pages the design puts the "Download instruction
     manual" link opposite the payment marks, on the same row. The theme renders
     it as its own block further down, so move it — the link and its href are
     untouched. */
  function initManualLink() {
    var pay = $('.kpod-pp-pay');
    if (!pay) return;
    var link = $$('a').filter(function (a) {
      return /instruction manual/i.test(a.textContent || '');
    })[0];
    if (!link || pay.contains(link)) return;
    var wrap = link.closest('.hdt-product-info__item') || link;
    var holder = document.createElement('span');
    holder.className = 'kpod-pp-manual';
    holder.appendChild(link);
    pay.appendChild(holder);
    if (wrap !== link && !wrap.textContent.trim()) wrap.style.display = 'none';
  }

  /* The delivery promise reads "Order in the next 21 Hours 39 Mins to get it
     between Friday, 4th September and Monday, 7th September" — long enough to
     wrap onto a second line and break the rhythm of the reassurance rows around
     it. The design's wording is shorter. The block's own settings are shared
     with control, so the text is shortened here instead, after order-delivery
     has populated the date spans. */
  var SHORT_DAYS = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

  function shortenDelivery() {
    var block = $('.hdt-product__order');
    if (!block) return;

    function tidy() {
      var text = block.querySelector('.hdt-s-text');
      if (text && /to get it between/.test(text.innerHTML)) {
        text.innerHTML = text.innerHTML.replace('to get it between', 'for delivery between');
      }
      ['.hdt-start_delivery', '.hdt-end_delivery'].forEach(function (sel) {
        var el = block.querySelector(sel);
        if (!el || el.dataset.kpodShort) return;
        var v = (el.textContent || '').trim();
        if (!v) return;
        // "Friday, 4th September" -> "Fri 4 September"
        var m = v.match(/^(\w+),\s*(\d+)(?:st|nd|rd|th)?\s*(.*)$/);
        if (m && SHORT_DAYS[m[1]]) {
          el.textContent = (SHORT_DAYS[m[1]] + ' ' + m[2] + ' ' + m[3]).trim();
          el.dataset.kpodShort = '1';
        }
      });
    }

    tidy();
    // order-delivery fills the dates in after its own script runs.
    var mo = new MutationObserver(tidy);
    mo.observe(block, { childList: true, subtree: true, characterData: true });
    window.setTimeout(function () { mo.disconnect(); }, 8000);
  }

  function initCtaRow() {
    var row = $('.hdt-product-form__top-buttons');
    if (!row) return;

    var qty = $('.hdt-product-form__quantity');
    if (qty && !row.contains(qty)) {
      var node = qty.closest('.hdt-product-info__item') || qty;
      row.insertBefore(node, row.firstChild);
    }

    /* Re-queried every call, never cached: the theme rebuilds the button's
       inner spans on variant change, which both reverts this text and detaches
       any stored reference. */
    var label = $('.hdt-btn-atc_text', row);
    if (label && /add to cart/i.test(label.textContent)) {
      // Design wording. The locale string is left alone so control is unchanged.
      label.textContent = label.textContent.replace(/add to cart/i, 'Add to basket');
    }

    var submit = $('.hdt-product-form__submit', row);
    if (submit && !$('.kpod-pp-atc-icon', submit)) {
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'kpod-pp-atc-icon');
      svg.setAttribute('viewBox', '0 0 17 18');
      svg.setAttribute('fill', 'currentColor');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.innerHTML = '<path d="M1.6 5.2h13.8l-1 11.2a1.6 1.6 0 0 1-1.6 1.4H4.2a1.6 1.6 0 0 1-1.6-1.4L1.6 5.2Z" ' +
        'fill="none" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M5.6 7V4.6a2.9 2.9 0 0 1 5.8 0V7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>';
      submit.insertBefore(svg, submit.firstChild);
    }
  }

  /* The theme re-renders the buy column after a variant change — it replaces
     the price block and the button's contents. That silently undoes every node
     we moved (quantity into the CTA row, per-pod and Klarna onto the price
     line, the manual link) and reverts the button label. So the layout is
     RE-asserted after each change rather than applied once.

     Deliberately NOT a MutationObserver: its callback is async, so an
     `applying` flag is already false again by the time it runs and every
     re-apply retriggers the observer — a feedback loop that consumed the main
     thread and left the page with nothing applied at all. Reacting to the
     theme's own change events instead is bounded and cannot recurse. */
  function applyLayout() {
    try {
      initValueCard();
      initPriceLine();
      initManualLink();
      initCtaRow();
      if (typeof repaintKit === 'function') repaintKit();
    } catch (e) {
      if (window.console) console.warn('[kpod-pp] layout re-apply failed', e);
    }
  }

  function watchBuyColumn() {
    var list = $('.hdt-product-info__list');
    if (!list) return;
    // One settle pass per change, at a few offsets, so we land after whichever
    // re-render the theme does. Each step is a no-op when already correct.
    function reassert() {
      [0, 80, 300, 700].forEach(function (ms) { window.setTimeout(applyLayout, ms); });
    }
    list.addEventListener('change', reassert);
    var form = $('form.hdt-main-product-form');
    if (form) form.addEventListener('change', reassert);
    document.addEventListener('cart:change', reassert);
  }

  // ------------------------------------------------------------------------
  // 1. Pack selector — default to the best-value pack, and report changes
  // ------------------------------------------------------------------------
  function initPacks() {
    if (!CFG || CFG.type !== 'pods') return;

    var cards = $('.hdt-ab-variant-cards');
    if (!cards) return;

    /* Deep links win: if the visitor ARRIVED on a specific variant (an ad, a
       shared link, a back-navigation) we must not silently move them onto a
       different, dearer pack.

       This must come from the bootstrap's reading of the ORIGINAL URL, taken in
       <head> before any theme script ran. The theme writes the selected variant
       into the URL on load, so reading location.search here would find a
       ?variant= on every page and suppress the default pack every time. */
    var deepLinked = !!(window.KPOD_PP && window.KPOD_PP.deepLinkedVariant);

    /* pod-pack-cards.liquid suppresses `checked` on this layer (the control
       picker owns the server-rendered selection, and both share one radio
       group). So first mirror whatever the control has checked onto the
       matching card — otherwise a deep-linked visitor sees a selector with
       nothing selected. Same sync the improved-variant-selector test does. */
    var picker = cards.closest('hdt-variant-picker') || document;
    var checkedControl = picker.querySelector('[data-ab-variant-selector="control"] input[is-value]:checked');
    if (checkedControl) {
      var mirror = $$('.hdt-pod-card__input', cards).filter(function (input) {
        return input.value === checkedControl.value;
      })[0];
      // Checking a card auto-unchecks the control radio: one shared group.
      if (mirror) mirror.checked = true;
    }

    if (!deepLinked && CFG.defaultPack) {
      var target = $$('.hdt-pod-card__input', cards).filter(function (input) {
        return String(input.value).toLowerCase() === String(CFG.defaultPack).toLowerCase()
          && !input.classList.contains('is-disabled');
      })[0];

      if (target && !target.checked) {
        target.checked = true;
        // <hdt-variant-picker> listens for change to update the price block,
        // the option label and [name=id] — exactly as a user click would.
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* The theme re-asserts its own (hidden) control radio after a variant
       change. Control and the cards share one radio group, so that silently
       unchecks the visible card: the form ends up on the right variant while no
       card looks selected. Mirror the group's real value back onto the cards
       once each change has settled.

       Deliberately NOT a MutationObserver — observing attribute changes while
       also setting .checked lets the theme and this code thrash against each
       other. A few bounded passes after the event is enough and cannot loop. */
    function syncCards() {
      var chosen = picker.querySelector('input[is-value]:checked');
      if (!chosen) return;
      /* Mirror the group's real value onto the cards as a CLASS. We do not set
         .checked here: the theme re-asserts its own control radio after every
         change, and setting .checked back just starts a tug of war that the
         theme eventually wins, leaving no card looking selected. */
      $$('.hdt-pod-card__input', cards).forEach(function (i) {
        var label = i.nextElementSibling;
        if (label) label.classList.toggle('is-pp-selected', i.value === chosen.value);
      });
      /* Anything derived from the selected pack has to be recomputed here too.
         The default-pack change fires before the change listener below exists,
         and the theme may briefly uncheck the card, so a single early call read
         the wrong pack — the free-delivery nudge was quoting the 1x24 price and
         telling shoppers to add £28.21 when the selected pack already cleared
         the threshold. */
      updateNudge();
      updatePerPod();
    }
    var syncTarget = picker === document ? document : picker;
    syncTarget.addEventListener('change', function () { window.setTimeout(syncCards, 0); });
    [0, 60, 250, 800].forEach(function (ms) { window.setTimeout(syncCards, ms); });

    /* The control radios share this group, so re-report whichever ends up
       checked rather than trusting our own click. */
    cards.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('hdt-pod-card__input')) return;
      var pack = packFor(input.value);
      capture('pp_pack_selected', {
        pack: input.value,
        variant_id: pack ? pack.variantId : null,
        unit_price: pack ? pack.price / 100 : null,
        price_per_pod: pack ? pack.perPod / 100 : null,
        pods: pack ? pack.pods : null
      });
      updateNudge();
      updatePerPod();
    });
  }

  // ------------------------------------------------------------------------
  // 2. Free-delivery nudge — reacts to the selected pack against the threshold
  // ------------------------------------------------------------------------
  function updateNudge() {
    var nudge = $('.kpod-pp-nudge');
    if (!nudge || !CFG || !CFG.freeShipThreshold) return;

    var input = selectedPackInput();
    var pack = input ? packFor(input.value) : null;
    var price = pack ? pack.price : (CFG.price || 0);
    var threshold = CFG.freeShipThreshold;
    var textEl = $('.kpod-pp-nudge__text', nudge);
    if (!textEl) return;

    if (price >= threshold) {
      nudge.classList.remove('kpod-pp-nudge--pending');
      textEl.textContent = 'Free UK delivery unlocked on this order.';
    } else {
      nudge.classList.add('kpod-pp-nudge--pending');
      textEl.textContent = 'Add ' + money(threshold - price) + ' more for free UK delivery.';
    }
  }

  /* "46p per pod · 96 pods" beside the price. */
  function updatePerPod() {
    var el = $('[data-kpod-pp-perpod-text]');
    if (!el) return;
    var input = selectedPackInput();
    var pack = input ? packFor(input.value) : null;
    if (!pack || !pack.pods) return;
    var per = pack.perPod < 100 ? pack.perPod + 'p' : money(pack.perPod);
    el.textContent = per + ' per pod \u00a0\u00b7\u00a0 ' + pack.pods + ' pods';
  }

  /* The design adds two lines to each pack card that pod-pack-cards.liquid does
     not render: the pod count alongside the per-pod price, and a
     "Free UK delivery included" flag on packs that clear the threshold. They are
     added here rather than in that snippet because the improved-variant-selector
     test renders the same markup, and changing its copy mid-flight would muddy
     its result. */
  function enrichPackCards() {
    if (!CFG || CFG.type !== 'pods' || !CFG.packs) return;
    var cards = $('.hdt-ab-variant-cards');
    if (!cards) return;

    $$('.hdt-pod-card__input', cards).forEach(function (input) {
      var pack = packFor(input.value);
      var label = input.nextElementSibling;
      if (!pack || !label || label.dataset.kpodEnriched) return;
      label.dataset.kpodEnriched = '1';

      var meta = label.querySelector('.hdt-pod-card__meta');
      if (meta && pack.pods && !/pods/.test(meta.textContent)) {
        // "49p per pod" -> "24 pods · 49p per pod" (no saving on this pack)
        if (!/Save/.test(meta.textContent)) {
          meta.textContent = pack.pods + ' pods \u00b7 ' + meta.textContent.trim();
        }
      }

      if (CFG.freeShipThreshold && pack.price >= CFG.freeShipThreshold) {
        var flag = document.createElement('span');
        flag.className = 'kpod-pp-packfree';
        flag.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
          'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5l5 5 11-11"/></svg>' +
          'Free UK delivery included';
        label.appendChild(flag);
      }
    });
  }

  // ------------------------------------------------------------------------
  // 3. Accordions (compatibility panel, FAQs, spec tables)
  // ------------------------------------------------------------------------
  /* "See all N FAQs" reveals the ones held back, in place. */
  function initFaqMore() {
    $$('[data-kpod-pp-faqs-more]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var root = btn.closest('.kpod-pp-faqs');
        if (!root) return;
        $$('.kpod-pp-acc--extra', root).forEach(function (el) { el.hidden = false; });
        btn.hidden = true;
        capture('pp_faq_open', { question: 'see_all' });
      });
    });
  }

  function initAccordions() {
    $$('[data-kpod-pp-acc]').forEach(function (root) {
      root.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('.kpod-pp-acc__q') : null;
        if (!btn || !root.contains(btn)) return;

        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');

        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = open;

        if (!open) {
          var kind = root.getAttribute('data-kpod-pp-acc'); // 'faq' | 'compat' | 'spec'
          var label = (btn.textContent || '').trim().slice(0, 120);
          if (kind === 'compat') capture('pp_compat_open', { question: label });
          else if (kind === 'spec') capture('pp_spec_view', { section: label, trigger: 'accordion' });
          else capture('pp_faq_open', { question: label });
        }
      });
    });
  }

  // ------------------------------------------------------------------------
  // 4. Mobile trust-row carousel
  // One item at a time, auto-advancing, looping, with position dots. Uses the
  // element's own scroll so a swipe stays native and the page never traps it.
  // ------------------------------------------------------------------------
  function initCarousel() {
    /* The trust row is the theme's own icon-box section — its content must not
       change, so we only take over its layout. Targeting by class rather than
       by the template's section id keeps this working if the section is
       re-added in the customiser. */
    var root = $('body.template-product .hdt-icon-box');
    if (!root) return;
    root.classList.add('kpod-pp-iconbox');

    var track = $('.hdt-icon-box__wrap', root);
    if (!track) return;

    var dots = $('.kpod-pp-dots', root);
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'kpod-pp-dots';
      dots.setAttribute('role', 'tablist');
      dots.setAttribute('aria-label', 'Trust highlights');
      track.parentNode.insertBefore(dots, track.nextSibling);
    }

    var mq = window.matchMedia('(max-width: 767.98px)');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var timer = null;
    var index = 0;
    var slides = $$('.hdt-icon-box__item', track);
    if (slides.length < 2) return;

    function paint() {
      $$('.kpod-pp-dot', dots).forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function go(i, smooth) {
      index = (i + slides.length) % slides.length;
      track.scrollTo({
        left: slides[index].offsetLeft - track.offsetLeft,
        behavior: smooth === false || reduce.matches ? 'auto' : 'smooth'
      });
      paint();
    }

    function stop() { if (timer) { window.clearInterval(timer); timer = null; } }

    function start() {
      stop();
      if (!mq.matches || reduce.matches || document.hidden) return;
      timer = window.setInterval(function () { go(index + 1); }, CAROUSEL_MS);
    }

    dots.innerHTML = '';
    slides.forEach(function (_, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'kpod-pp-dot';
      b.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + slides.length);
      b.addEventListener('click', function () { go(i); start(); });
      dots.appendChild(b);
    });
    paint();

    /* Keep the dots honest when the visitor swipes, and pause while they are
       interacting or the tab is in the background. */
    var scrollDebounce;
    track.addEventListener('scroll', function () {
      window.clearTimeout(scrollDebounce);
      scrollDebounce = window.setTimeout(function () {
        var nearest = 0;
        var best = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs((s.offsetLeft - track.offsetLeft) - track.scrollLeft);
          if (d < best) { best = d; nearest = i; }
        });
        index = nearest;
        paint();
      }, 90);
    }, { passive: true });

    ['pointerdown', 'touchstart', 'mouseenter', 'focusin'].forEach(function (ev) {
      track.addEventListener(ev, stop, { passive: true });
    });
    ['pointerup', 'touchend', 'mouseleave', 'focusout'].forEach(function (ev) {
      track.addEventListener(ev, start, { passive: true });
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
    if (mq.addEventListener) mq.addEventListener('change', function () { go(0, false); start(); });

    start();
  }

  // ------------------------------------------------------------------------
  // 5. Mix-and-match case builder (pods page)
  // Adds four 1x24 boxes as separate line items. No price promise is made —
  // four mixed boxes cost the sum of their parts, which is dearer than the
  // 4x24 pack of a single blend, so the copy sells variety and free delivery.
  // ------------------------------------------------------------------------
  function initCaseBuilder() {
    var root = $('[data-kpod-pp-case]');
    if (!root) return;

    var target = parseInt(root.getAttribute('data-case-size'), 10) || 4;
    var submit = $('.kpod-pp-case__submit', root);
    var countEl = $('.kpod-pp-case__count', root);
    var totalEl = $('.kpod-pp-case__total', root);
    var chosen = [];

    function paint() {
      var total = chosen.reduce(function (sum, c) { return sum + c.price; }, 0);
      if (countEl) countEl.textContent = chosen.length + ' of ' + target + ' boxes chosen';
      // The design puts the running total on the button itself.
      if (totalEl) totalEl.textContent = total ? ' \u2013 ' + money(total) : '';
      submit.disabled = chosen.length !== target;
      $$('.kpod-pp-case__btn', root).forEach(function (btn) {
        var id = btn.getAttribute('data-variant-id');
        var picked = chosen.some(function (c) { return c.variantId === id; });
        btn.setAttribute('aria-pressed', picked ? 'true' : 'false');
        btn.innerHTML = picked
          ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" ' +
            'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align:-2px;margin-right:6px">' +
            '<path d="M4 12.5l5 5 11-11"/></svg>In your case'
          : 'Add to case';
        btn.disabled = !picked && chosen.length >= target;
        var card = btn.closest('.kpod-pp-case__card');
        if (card) card.classList.toggle('kpod-pp-case__card--in', picked);
      });
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.kpod-pp-case__btn') : null;
      if (!btn || !root.contains(btn)) return;

      var id = btn.getAttribute('data-variant-id');
      var price = parseInt(btn.getAttribute('data-price'), 10) || 0;
      var title = btn.getAttribute('data-title') || '';
      var at = chosen.findIndex(function (c) { return c.variantId === id; });

      if (at > -1) chosen.splice(at, 1);
      else if (chosen.length < target) chosen.push({ variantId: id, price: price, title: title });

      paint();
      capture('pp_case_add', {
        blend: title,
        variant_id: id,
        action: at > -1 ? 'remove' : 'add',
        case_size: chosen.length,
        case_value: chosen.reduce(function (s, c) { return s + c.price; }, 0) / 100
      });
    });

    submit.addEventListener('click', function () {
      if (chosen.length !== target || submit.disabled) return;
      submit.disabled = true;

      var lineValue = chosen.reduce(function (s, c) { return s + c.price; }, 0);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: chosen.map(function (c) { return { id: c.variantId, quantity: 1 }; })
        })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart/add failed: ' + r.status);
          return r.json();
        })
        .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          capture('pp_add_to_cart', {
            source: 'case_builder',
            pack: 'mixed-case-' + target,
            line_value: lineValue / 100,
            cart_value: cart.total_price / 100,
            blends: chosen.map(function (c) { return c.title; })
          });

          // Let the theme refresh its cart UI, then open the drawer the same
          // way the header trigger does.
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cart }
          }));
          var trigger = $('[aria-controls="CartDrawer"]');
          if (trigger) trigger.click();
        })
        .catch(function (err) {
          if (window.console) console.warn('[kpod-pp] case builder add failed', err);
          var msg = $('.kpod-pp-case__error', root);
          if (msg) { msg.hidden = false; msg.textContent = 'Sorry — that did not add. Please try again.'; }
        })
        .then(function () { submit.disabled = chosen.length !== target; });
    });

    paint();
  }

  // ------------------------------------------------------------------------
  // 6. Kit selector (machine page)
  // Machine-only and the bundle are two different products. The buy form posts
  // whatever variant id is in [name=id], so switching kit swaps that id and
  // repaints the price. Shop Pay reads the same field, so it follows along.
  // ------------------------------------------------------------------------
  var repaintKit = null;

  function initKitSelector() {
    var root = $('[data-kpod-pp-kit]');
    if (!root || !CFG || !CFG.kits) return;

    var form = $('form.hdt-main-product-form');
    var idInput = form ? $('input[name="id"]', form) : null;
    var priceNow = $('.kpod-pp-kit-price__now');
    var priceWas = $('.kpod-pp-kit-price__was');
    var atcLabel = $('.hdt-product-form__submit .hdt-btn-atc_text');
    /* Re-queried on every use, never cached: <hdt-qty-price> replaces its inner
       .hdt-money span when it re-renders, so a reference taken at init time ends
       up detached and writing to it silently does nothing. */
    function qtyPriceEl() { return $('.hdt-product-form__submit .hdt-qty-price'); }
    function qtyMoneyEl() { return $('.hdt-product-form__submit .hdt-qty-price .hdt-money'); }

    function kitFor(key) {
      for (var i = 0; i < CFG.kits.length; i++) {
        if (CFG.kits[i].key === key) return CFG.kits[i];
      }
      return null;
    }

    function apply(key, report) {
      var kit = kitFor(key);
      if (!kit) return;

      if (idInput) {
        idInput.value = kit.variantId;
        idInput.disabled = false;
      }
      if (priceNow) priceNow.textContent = money(kit.price);
      if (priceWas) {
        priceWas.textContent = kit.compareAt > kit.price ? money(kit.compareAt) : '';
        priceWas.hidden = !(kit.compareAt > kit.price);
      }
      if (atcLabel && kit.atcLabel) atcLabel.textContent = kit.atcLabel;

      /* <hdt-qty-price> recomputes the suffix on the button from its own
         variant-price attribute whenever quantity or variant changes. The theme
         still believes this page is the machine, so it would rewrite £139 back
         to £129. Set the ATTRIBUTE it reads from as well as the rendered text,
         and re-assert a few times as it re-renders. */
      var want = money(kit.price);
      function paintQtyPrice() {
        var host = qtyPriceEl();
        if (host) host.setAttribute('variant-price', kit.price);
        var el = qtyMoneyEl();
        if (el && el.textContent.indexOf(want) === -1) el.textContent = want;
      }
      paintQtyPrice();
      [30, 120, 400, 900].forEach(function (ms) { window.setTimeout(paintQtyPrice, ms); });

      root.setAttribute('data-kit-selected', key);

      if (report) {
        capture('pp_kit_selected', {
          kit: key,
          variant_id: kit.variantId,
          unit_price: kit.price / 100,
          compare_at: kit.compareAt ? kit.compareAt / 100 : null
        });
      }
    }

    root.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('kpod-pp-kit__input')) return;
      apply(input.value, true);
    });

    var checked = $('.kpod-pp-kit__input:checked', root);
    if (checked) apply(checked.value, false);

    /* Let the buy-column watcher restore the kit's label and price after the
       theme rebuilds the button. */
    repaintKit = function () {
      var sel = root.getAttribute('data-kit-selected');
      if (sel) apply(sel, false);
    };
  }

  // ------------------------------------------------------------------------
  // 7. Add-to-cart reporting (theme form) + upsell clicks
  // ------------------------------------------------------------------------
  function initAtcTracking() {
    var form = $('form.hdt-main-product-form');
    if (!form) return;

    form.addEventListener('submit', function () {
      var idInput = $('input[name="id"]', form);
      var qtyInput = $('[name="quantity"]', form);
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      var variantId = idInput && !idInput.disabled ? idInput.value : null;

      var pack = null;
      var unit = CFG ? CFG.price : 0;

      if (CFG && CFG.type === 'pods') {
        var input = selectedPackInput();
        if (input) {
          pack = input.value;
          var p = packFor(pack);
          if (p) { unit = p.price; variantId = variantId || p.variantId; }
        }
      } else if (CFG && CFG.type === 'machine') {
        var kitRoot = $('[data-kpod-pp-kit]');
        var key = kitRoot ? kitRoot.getAttribute('data-kit-selected') : null;
        pack = key;
        if (key && CFG.kits) {
          CFG.kits.forEach(function (k) { if (k.key === key) unit = k.price; });
        }
      }

      capture('pp_add_to_cart', {
        source: 'buy_box',
        variant_id: variantId,
        pack: pack,
        quantity: qty,
        unit_price: unit / 100,
        line_value: (unit * qty) / 100
      });
    });
  }

  function initUpsellTracking() {
    $$('[data-kpod-pp-upsell]').forEach(function (el) {
      el.addEventListener('click', function () {
        capture('pp_upsell_click', {
          placement: el.getAttribute('data-kpod-pp-upsell'),
          target_handle: el.getAttribute('data-target-handle') || null
        });
      });
    });
  }

  // ------------------------------------------------------------------------
  // 8. Reviews — relocate the live Judge.me widget
  // The widget is MOVED, never cloned: #judgeme_product_reviews is a unique id
  // and duplicating it breaks the app. Same technique the existing
  // "expanded-sections" experiment documents. If the widget has not mounted yet
  // we retry briefly, and if it never appears the band simply shows its header.
  // ------------------------------------------------------------------------
  function initReviews() {
    var slot = $('[data-kpod-pp-reviews-slot]');
    if (!slot) return;

    /* "Read all N reviews" lifts the two-review limit in place. Judge.me's own
       histogram rows stay clickable, so star filtering keeps working — we only
       make sure filtering also reveals the full list rather than leaving the
       reader looking at two of a filtered set. */
    var more = $('[data-kpod-pp-reviews-more]');
    function expand() {
      slot.removeAttribute('data-visible');
      if (more) more.hidden = true;
    }
    if (more) {
      more.addEventListener('click', function () {
        expand();
        capture('pp_reviews_expand', { review_count: CFG ? CFG.reviewCount : null });
      });
    }
    var hist = $('[data-kpod-pp-histogram-slot]');
    if (hist) {
      hist.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.jdgm-histogram__row')) expand();
      });
    }

    var tries = 0;
    (function attempt() {
      var widget = document.getElementById('judgeme_product_reviews')
        || $('.jdgm-review-widget')
        || $('.jdgm-rev-widg');

      if (widget && !slot.contains(widget)) {
        slot.appendChild(widget);          // move the live node

        // The design puts Judge.me's star distribution beside the score rather
        // than above the review list, so move that one node across too.
        var hist = document.querySelector('[data-kpod-pp-histogram-slot]');
        var histogram = widget.querySelector('.jdgm-histogram');
        if (hist && histogram) hist.appendChild(histogram);

        widget.style.display = '';         // the control tab may have hidden it
        // Only now is it safe to fully hide the tabs section the widget came
        // from — see section 17 of kpod-pp-redesign.css.
        document.documentElement.classList.add('kpod-pp-reviews-moved');
        return;
      }
      if (!widget && tries++ < PACK_RETRY_MAX) {
        window.setTimeout(attempt, PACK_RETRY_MS);
      }
    })();
  }

  // ------------------------------------------------------------------------
  // 9. Spec-table visibility (engagement with the reassurance modules)
  // ------------------------------------------------------------------------
  function initSpecView() {
    var spec = $('[data-kpod-pp-spec]');
    onceVisible(spec, function () {
      capture('pp_spec_view', { section: 'specifications', trigger: 'scroll' });
    });
  }

  // ------------------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------------------
  var started = false;

  function start() {
    if (started || !CFG) return;
    started = true;

    /* Mirror the product type onto <html> so page-specific CSS has a hook that
       does not depend on :has() support. */
    if (CFG.type) document.documentElement.setAttribute('data-pp-type', CFG.type);

    /* Belt and braces on the reveal. The treatment markup ships with [hidden]
       and is normally un-hidden purely in CSS (that is what keeps it
       flicker-free). But a CSS-only reveal is a single point of failure — if it
       loses for any reason the block silently stays invisible, which is how the
       bundle ended up with no price at all. Once the treatment is confirmed,
       drop the attribute outright so the markup cannot stay hidden. */
    $$('[data-kpod-pp], [data-kpod-pp-flex], [data-kpod-pp-grid]').forEach(function (el) {
      el.removeAttribute('hidden');
    });

    /* The redesign only exists where its markup was actually rendered. Other
       product templates (product.black-friday-product, product.only_config)
       share sections/main-product.liquid and therefore this config blob, but
       carry none of the treatment — firing pp_view there would credit the
       experiment with views nobody in it ever saw. */
    if (!document.querySelector('.kpod-pp-below')) return;

    capture('pp_view', {
      price: CFG.price / 100,
      default_pack: CFG.defaultPack || null,
      viewport: window.innerWidth < 768 ? 'mobile' : 'desktop'
    });

    /* The buy column hydrates slightly after flags resolve, so retry the pack
       default briefly rather than assuming the picker already exists. */
    var tries = 0;
    (function attempt() {
      if ($('.hdt-ab-variant-cards') || CFG.type !== 'pods' || tries++ > PACK_RETRY_MAX) {
        initPacks();
        enrichPackCards();
        updateNudge();
        updatePerPod();
        return;
      }
      window.setTimeout(attempt, PACK_RETRY_MS);
    })();

    initOrder();
    initValueCard();
    initPriceLine();
    initManualLink();
    shortenDelivery();
    initCtaRow();
    initAccordions();
    initFaqMore();
    initCarousel();
    initReviews();
    initCaseBuilder();
    initKitSelector();
    initAtcTracking();
    initUpsellTracking();
    initSpecView();
    watchBuyColumn();
  }

  function onVariant(variant) {
    if (variant !== TREATMENT) return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  // The resolver in theme.liquid may have settled before this script parsed.
  if (window.KPOD_PP && window.KPOD_PP.resolved) {
    onVariant(window.KPOD_PP.variant);
  } else {
    /* NOT {once:true}: the resolver re-reads the flag and can upgrade a visitor
       from control to redesign a moment after the first answer. Dropping the
       listener after one event meant that upgrade was never acted on — the
       markup was revealed by CSS but none of the behaviour ever ran. start() is
       guarded by `started`, so extra events are harmless. */
    document.addEventListener('kpod:pp-variant', function (e) {
      onVariant(e.detail && e.detail.variant);
    });

    /* Belt and braces: if <html data-pp-variant> already says redesign but the
       event never reached us (script parsed late, CustomEvent unavailable, the
       resolver replaced), start anyway. onVariant/start are idempotent-guarded
       by the .kpod-pp-below check, and started is checked below. */
    document.addEventListener('DOMContentLoaded', function () {
      if (!started && document.documentElement.getAttribute('data-pp-variant') === TREATMENT) {
        onVariant(TREATMENT);
      }
    }, { once: true });

    /* Final safety net. If <html> ends up saying redesign but start() never
       ran — any ordering we have not thought of — the page would show the
       treatment markup with none of its behaviour: no price move, dead
       accordions, duplicated price. Poll briefly and catch it. */
    var sweeps = 0;
    var sweep = window.setInterval(function () {
      if (started || sweeps++ > 20) { window.clearInterval(sweep); return; }
      if (document.documentElement.getAttribute('data-pp-variant') === TREATMENT) {
        window.clearInterval(sweep);
        onVariant(TREATMENT);
      }
    }, 250);
  }
})();
